import { useEffect, useRef } from 'react';
import { useGetDWRequestQuery } from '@/store/api/bankerApi';
import { BankerRequestItem } from '@/types';

type RequestKind = 'Credit' | 'Debit';

const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification_sound.mp3');
    void audio.play().catch(() => {
      // Audio can be blocked by a browser until the user has interacted with the page.
    });
  } catch {
    // Audio can be blocked by a browser until the user has interacted with the page.
  }
};

/**
 * Plays a bell only for requests received after the initial data load.
 * The two request types are watched separately so deposits and withdrawals
 * arriving in the same polling cycle still result in a single notification.
 */
export const useDepositWithdrawRequestSound = (enabled: boolean) => {
  const queryOptions = { skip: !enabled, pollingInterval: 5000 };
  const { data: depositData } = useGetDWRequestQuery(
    { type: 'Credit', status: 'Pending', page: 1, limit: 100, search: '' },
    queryOptions
  );
  const { data: withdrawalData } = useGetDWRequestQuery(
    { type: 'Debit', status: 'Pending', page: 1, limit: 100, search: '' },
    queryOptions
  );
  const knownRequestIds = useRef<Record<RequestKind, Set<string>>>({
    Credit: new Set(),
    Debit: new Set(),
  });
  const hasLoadedInitialData = useRef<Record<RequestKind, boolean>>({ Credit: false, Debit: false });

  useEffect(() => {
    const checkForNewRequests = (kind: RequestKind, requests?: BankerRequestItem[]) => {
      if (!requests) return false;

      const requestIds = requests.filter((request) => request.status === 'Pending').map((request) => request.id);
      const knownIds = knownRequestIds.current[kind];

      if (!hasLoadedInitialData.current[kind]) {
        requestIds.forEach((id) => knownIds.add(id));
        hasLoadedInitialData.current[kind] = true;
        return false;
      }

      const hasNewRequest = requestIds.some((id) => !knownIds.has(id));
      requestIds.forEach((id) => knownIds.add(id));

      return hasNewRequest;
    };

    const hasNewDeposit = checkForNewRequests('Credit', depositData?.data);
    const hasNewWithdrawal = checkForNewRequests('Debit', withdrawalData?.data);

    if (hasNewDeposit || hasNewWithdrawal) playNotificationSound();
  }, [depositData, withdrawalData]);
};
